import AsyncStorage from '@react-native-async-storage/async-storage';
import { chatService } from '@/features/chat/chatService';
import { supabase } from '@/utils/supabase';
import type { Course, SearchData } from '@/types/chat';

export const QUICK_FLOW_TOTAL_STEPS = 4;
const STORAGE_KEY = 'kursi_quick_flow_v1';

export type QuickFlowFocus = 'main_field' | 'sub_field' | 'level' | 'goal';

export interface QuickFlowOption {
  id: string;
  label: string;
  value: string;
}

export interface QuickFlowStep {
  step: number;
  focus: QuickFlowFocus;
  question: string;
  options: QuickFlowOption[];
  source: 'fixed' | 'ai' | 'fallback';
}

export interface QuickFlowAnswer {
  step: number;
  question: string;
  focus: QuickFlowFocus;
  option: QuickFlowOption;
}

export interface QuickFlowStateSnapshot {
  currentStep: number;
  steps: QuickFlowStep[];
  answers: QuickFlowAnswer[];
}

const FIXED_STEP_1: QuickFlowStep = {
  step: 1,
  focus: 'main_field',
  source: 'fixed',
  question: 'איזה תחום הכי מעניין אותך עכשיו?',
  options: [
    { id: 'tech', label: 'הייטק ופיתוח', value: 'הייטק ופיתוח' },
    { id: 'marketing', label: 'שיווק ודיגיטל', value: 'שיווק ודיגיטל' },
    { id: 'business', label: 'עסקים ויזמות', value: 'עסקים ויזמות' },
    { id: 'finance', label: 'פיננסים ושוק ההון', value: 'פיננסים ושוק ההון' },
    { id: 'design', label: 'עיצוב ויצירה', value: 'עיצוב ויצירה' },
  ],
};

const FALLBACK_STEPS: Record<number, Omit<QuickFlowStep, 'step'>> = {
  2: {
    focus: 'sub_field',
    source: 'fallback',
    question: 'איזה כיוון הכי מדבר אליך בתוך התחום?',
    options: [
      { id: 'sub_practical', label: 'כיוון פרקטי לעבודה', value: 'כיוון פרקטי לעבודה' },
      { id: 'sub_specialization', label: 'התמחות מקצועית', value: 'התמחות מקצועית' },
      { id: 'sub_basics', label: 'להבין את היסודות', value: 'להבין את היסודות' },
      { id: 'sub_tools', label: 'כלים ושיטות עבודה', value: 'כלים ושיטות עבודה' },
    ],
  },
  3: {
    focus: 'level',
    source: 'fallback',
    question: 'מה רמת הניסיון שלך בתחום?',
    options: [
      { id: 'lvl_new', label: 'חדש לי לגמרי', value: 'חדש לי לגמרי' },
      { id: 'lvl_some', label: 'יש לי בסיס', value: 'יש לי בסיס' },
      { id: 'lvl_exp', label: 'יש לי ניסיון', value: 'יש לי ניסיון' },
      { id: 'lvl_adv', label: 'מתקדם ורוצה לעלות רמה', value: 'מתקדם ורוצה לעלות רמה' },
    ],
  },
  4: {
    focus: 'goal',
    source: 'fallback',
    question: 'מה המטרה המרכזית שלך מהקורס?',
    options: [
      { id: 'goal_job', label: 'להתקבל לתפקיד חדש', value: 'להתקבל לתפקיד חדש' },
      { id: 'goal_upgrade', label: 'להתקדם בעבודה הנוכחית', value: 'להתקדם בעבודה הנוכחית' },
      { id: 'goal_project', label: 'לבנות פרויקט אישי', value: 'לבנות פרויקט אישי' },
      { id: 'goal_confidence', label: 'להרגיש ביטחון מקצועי', value: 'להרגיש ביטחון מקצועי' },
    ],
  },
};

function sanitizeText(v: unknown): string {
  return (v ?? '').toString().replace(/\s+/g, ' ').trim();
}

function isHebrewText(v: string): boolean {
  return /[\u0590-\u05FF]/.test(v);
}

function normalizeOption(opt: any, index: number): QuickFlowOption | null {
  const label = sanitizeText(opt?.label ?? opt?.value);
  const value = sanitizeText(opt?.value ?? opt?.label);
  const id = sanitizeText(opt?.id) || `opt_${index + 1}`;
  if (!label || !value) return null;
  if (!isHebrewText(label)) return null;
  return { id: id.slice(0, 40), label: label.slice(0, 54), value: value.slice(0, 120) };
}

function validateGeneratedStep(stepNumber: number, raw: any): QuickFlowStep | null {
  const question = sanitizeText(raw?.question);
  const focus = sanitizeText(raw?.focus) as QuickFlowFocus;
  const optionsRaw = Array.isArray(raw?.options) ? raw.options : [];
  if (!question || question.length < 6 || question.length > 120 || !isHebrewText(question)) return null;
  if (!['sub_field', 'level', 'goal'].includes(focus)) return null;

  const uniqueLabels = new Set<string>();
  const options: QuickFlowOption[] = [];
  for (let i = 0; i < optionsRaw.length; i += 1) {
    const normalized = normalizeOption(optionsRaw[i], i);
    if (!normalized) continue;
    const key = normalized.label.toLowerCase();
    if (uniqueLabels.has(key)) continue;
    uniqueLabels.add(key);
    options.push(normalized);
    if (options.length === 5) break;
  }

  if (options.length < 3) return null;

  return {
    step: stepNumber,
    focus,
    question,
    options,
    source: 'ai',
  };
}

export function getFallbackStep(stepNumber: number): QuickFlowStep {
  const base = FALLBACK_STEPS[stepNumber as 2 | 3 | 4] ?? FALLBACK_STEPS[4];
  return { step: stepNumber, ...base };
}

export async function generateDynamicStep(stepNumber: number, answers: QuickFlowAnswer[]): Promise<QuickFlowStep> {
  const payload = {
    step: stepNumber,
    answers: answers.map((a) => ({
      step: a.step,
      focus: a.focus,
      question: a.question,
      selected_label: a.option.label,
      selected_value: a.option.value,
    })),
  };

  try {
    const { data, error } = await supabase.functions.invoke('chat-quick-step', { body: payload });
    if (error) throw error;
    const valid = validateGeneratedStep(stepNumber, data);
    return valid ?? getFallbackStep(stepNumber);
  } catch (err) {
    console.error('[quickFlow] generateDynamicStep failed', err);
    return getFallbackStep(stepNumber);
  }
}

export function buildRecommendationContext(answers: QuickFlowAnswer[]): SearchData {
  const byFocus = Object.fromEntries(answers.map((a) => [a.focus, a.option.value])) as Record<QuickFlowFocus, string>;

  const topic = byFocus.main_field || answers[0]?.option.value || 'קורס מקצועי';
  const subtopic = byFocus.sub_field || '';
  const experienceLevel = byFocus.level || '';
  const goal = byFocus.goal || '';

  const summary = [
    `תחום מרכזי: ${topic}`,
    subtopic ? `כיוון רצוי: ${subtopic}` : '',
    experienceLevel ? `רמה נוכחית: ${experienceLevel}` : '',
    goal ? `מטרה מרכזית: ${goal}` : '',
    'העדפה: התאמה מהירה לפי שאלון 4 צעדים ללא הקלדה.',
  ]
    .filter(Boolean)
    .join(' | ');

  return {
    topic,
    subtopic,
    experience_level: experienceLevel,
    goals: goal,
    learning_style: 'guided_quick_flow',
    query_type: 'guided_quick_flow',
    summary,
  };
}

export async function runQuickFlowRecommendation(answers: QuickFlowAnswer[]): Promise<{ matchCount: number; userSummary: string; courses?: Course[]; searchData: SearchData; }> {
  const searchData = buildRecommendationContext(answers);
  const result = await chatService.sendToWebhook(searchData);
  return { ...result, searchData };
}

export async function persistQuickFlowState(snapshot: QuickFlowStateSnapshot): Promise<void> {
  try {
    await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch (err) {
    console.error('[quickFlow] persist failed', err);
  }
}

export async function loadQuickFlowState(): Promise<QuickFlowStateSnapshot | null> {
  try {
    const raw = await AsyncStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (!Array.isArray(parsed.steps) || !Array.isArray(parsed.answers)) return null;
    return {
      currentStep: Number(parsed.currentStep) || 1,
      steps: parsed.steps,
      answers: parsed.answers,
    };
  } catch {
    return null;
  }
}

export async function clearQuickFlowState(): Promise<void> {
  try {
    await AsyncStorage.removeItem(STORAGE_KEY);
  } catch {}
}

export function getInitialQuickFlowSnapshot(): QuickFlowStateSnapshot {
  return { currentStep: 1, steps: [FIXED_STEP_1], answers: [] };
}
